import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { serviceLabel, statusLabel, statusStyle, whatsappHref } from "@/lib/format";
import { Search, MessageCircle, Trash2, Loader2, ImageDown, Star, Truck } from "lucide-react";

const STATUSES = ["nueva", "contactada", "confirmada", "cancelada"];

const GOOGLE_REVIEW_LINK = "https://search.google.com/local/writereview?placeid=ChIJle2aZJ-bP44RIpwE1qY6wH4";
const SERVICE_FORM_LINK = "https://docs.google.com/forms/d/e/1FAIpQLSdIBsDXwioCslyD69qHThEkYbE4EMSEHydcoIpBmIe71cyt1Q/viewform?usp=header";

export default function QuotesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", service_type: "", q: "" });
  const [selected, setSelected] = useState(null);
  const [precioInput, setPrecioInput] = useState("");
  const [fechaFinInput, setFechaFinInput] = useState("");
  const [generando, setGenerando] = useState(false);
  const [fleet, setFleet] = useState([]);
  const [vehiculoInput, setVehiculoInput] = useState("");
  const [placaInput, setPlacaInput] = useState("");
  const [movilInput, setMovilInput] = useState("");
  const [conductorNombreInput, setConductorNombreInput] = useState("");
  const [conductorTelefonoInput, setConductorTelefonoInput] = useState("");
  const [guardandoAsignacion, setGuardandoAsignacion] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.service_type) params.service_type = filter.service_type;
      if (filter.q) params.q = filter.q;
      const { data } = await api.get("/admin/quotes", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.service_type]);

  useEffect(() => {
    api.get("/admin/fleet").then(({ data }) => setFleet(Array.isArray(data) ? data : [])).catch(() => setFleet([]));
  }, []);

  // Limpia los formularios cada vez que se abre una solicitud distinta, precargando la asignación si ya existía
  useEffect(() => {
    setPrecioInput("");
    setFechaFinInput("");
    setVehiculoInput(selected?.assigned_vehicle || "");
    setPlacaInput(selected?.vehicle_plate || "");
    setMovilInput(selected?.vehicle_movil || "");
    setConductorNombreInput(selected?.driver_name || "");
    setConductorTelefonoInput(selected?.driver_phone || "");
  }, [selected?.id]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, { status });
      setRows((rs) => (Array.isArray(rs) ? rs : []).map((r) => r.id === id ? data : r));
      if (selected?.id === id) setSelected(data);
      toast.success(`Marcada como ${statusLabel(status).toLowerCase()}`);
    } catch { toast.error("Error al actualizar"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setRows((rs) => (Array.isArray(rs) ? rs : []).filter((r) => r.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Cotización eliminada");
    } catch { toast.error("Error al eliminar"); }
  };

  const generarCotizacion = async () => {
    if (!selected) return;
    if (!precioInput.trim()) {
      toast.error("Escribe el precio antes de generar la imagen");
      return;
    }
    setGenerando(true);
    try {
      const resp = await api.post(
        `/admin/quotes/${selected.id}/cotizacion-imagen`,
        { precio: precioInput.trim(), fecha_fin: fechaFinInput.trim() || undefined },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cotizacion_${selected.name.replace(/\s+/g, "_")}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Cotización generada — revisa tus descargas");
    } catch {
      toast.error("No se pudo generar la cotización");
    } finally {
      setGenerando(false);
    }
  };

  const guardarAsignacion = async () => {
    if (!selected) return;
    setGuardandoAsignacion(true);
    try {
      const { data } = await api.patch(`/admin/quotes/${selected.id}`, {
        assigned_vehicle: vehiculoInput || undefined,
        vehicle_plate: placaInput || undefined,
        vehicle_movil: movilInput || undefined,
        driver_name: conductorNombreInput || undefined,
        driver_phone: conductorTelefonoInput || undefined,
      });
      setRows((rs) => (Array.isArray(rs) ? rs : []).map((r) => r.id === selected.id ? data : r));
      setSelected(data);
      toast.success("Asignación guardada");
    } catch {
      toast.error("No se pudo guardar la asignación");
    } finally {
      setGuardandoAsignacion(false);
    }
  };

  const fichaMessage = (q) => {
    const label = serviceLabel(q.service_type);
    const lineas = [
      `🚌 FICHA DE SERVICIO — Líneas Doradas`,
      ``,
      `Cliente: ${q.name} (${q.whatsapp})`,
      `Servicio: ${label}`,
      `Origen: ${q.origin}`,
      `Destino: ${q.destination || (q.service_type === "por_horas" ? `${q.total_hours || "?"} h por horas` : "—")}`,
      `Fecha: ${q.date}${q.time ? " · " + q.time : ""}`,
      `Pasajeros: ${q.passengers}`,
    ];
    if (q.flight_number) lineas.push(`Vuelo: ${q.flight_number}`);
    if (q.assigned_vehicle) lineas.push(`Vehículo asignado: ${q.assigned_vehicle}`);
    if (q.vehicle_plate) lineas.push(`Placa: ${q.vehicle_plate}`);
    if (q.vehicle_movil) lineas.push(`Móvil: ${q.vehicle_movil}`);
    if (q.notes) lineas.push(`Notas: ${q.notes}`);
    lineas.push(``, `Cualquier duda, escríbeme por aquí.`);
    return lineas.join("\n");
  };

  const waMessage = (q) => `Hola ${q.name}, te escribo de Líneas Doradas por tu solicitud de ${serviceLabel(q.service_type)} del ${q.date}. ¿Podemos ayudarte a coordinar?`;

  const formMessage = (q) => `¡Hola ${q.name}! Tu cotización con Líneas Doradas ya fue aprobada 🎉\n\nPara coordinar tu servicio, por favor completa este formulario con los datos necesarios para el viaje:\n${SERVICE_FORM_LINK}\n\nEn cuanto lo recibamos, seguimos con la programación del vehículo y el conductor. ¡Gracias!`;

  const reviewMessage = (q) => `Hola ${q.name}! 🌟 Muchas gracias por viajar con Líneas Doradas. Esperamos que hayas tenido una excelente experiencia. ¿Nos ayudas dejando una reseña en Google? Toma menos de 1 minuto: ${GOOGLE_REVIEW_LINK}\n\nSi prefieres, también puedes responder aquí mismo con tu comentario y lo publicamos con gusto en nuestra página web. ¡Gracias por confiar en nosotros! 🚌`;

  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <div data-testid="quotes-admin">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-2">Cotizaciones</div>
          <h1 className="font-display text-3xl md:text-4xl">Solicitudes recibidas</h1>
        </div>
        <div className="text-sm text-[#F5EFE0]/55">{safeRows.length} resultados</div>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
          <input data-testid="filter-search" placeholder="Buscar…" value={filter.q}
                 onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 className="bg-[#171412] border border-[#3A3229] rounded-lg pl-9 pr-3 py-2 text-sm w-56 focus:border-[#D4AF37] outline-none" />
        </div>
        <select data-testid="filter-status" className="bg-[#171412] border border-[#3A3229] rounded-lg px-3 py-2 text-sm text-[#F5EFE0]"
                value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">Todos los estados</option>
          {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        <select data-testid="filter-service" className="bg-[#171412] border border-[#3A3229] rounded-lg px-3 py-2 text-sm text-[#F5EFE0]"
                value={filter.service_type} onChange={(e) => setFilter({ ...filter, service_type: e.target.value })}>
          <option value="">Todos los servicios</option>
          <option value="aeropuerto">Aeropuerto</option>
          <option value="ciudad">Ciudad</option>
          <option value="fuera_ciudad">Fuera ciudad</option>
          <option value="por_horas">Por horas</option>
        </select>
      </div>

      <div className="card-lux overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#F5EFE0]/55 flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Cargando…</div>
        ) : safeRows.length === 0 ? (
          <div className="p-10 text-center text-[#F5EFE0]/55">Aún no hay cotizaciones que coincidan.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#171412] text-[#D4AF37] text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Cliente</th>
                  <th className="px-4 py-3 text-left">Servicio</th>
                  <th className="px-4 py-3 text-left">Ruta</th>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {safeRows.map((q) => (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-t border-[#3A3229] hover:bg-[#D4AF37]/5">
                    <td className="px-4 py-3">
                      <button onClick={() => setSelected(q)} className="text-left">
                        <div className="text-[#F5EFE0] font-medium">{q.name}</div>
                        <div className="text-xs text-[#F5EFE0]/50">{q.whatsapp}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-[#F5EFE0]/80">{serviceLabel(q.service_type)}</td>
                    <td className="px-4 py-3 text-[#F5EFE0]/80">
                      {q.origin} → {q.destination || (q.service_type === "por_horas" ? `${q.total_hours || "?"} h` : "—")}
                    </td>
                    <td className="px-4 py-3 text-[#F5EFE0]/80">
                      <div>{q.date}</div>
                      <div className="text-xs text-[#F5EFE0]/50">{q.time || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select value={q.status} onChange={(e) => updateStatus(q.id, e.target.value)}
                              data-testid={`status-select-${q.id}`}
                              className={`text-xs px-2.5 py-1.5 rounded-full border ${statusStyle(q.status)} bg-transparent focus:outline-none`}>
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-[#171412] text-[#F5EFE0]">{statusLabel(s)}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        {q.status === "confirmada" && (
                          <a href={whatsappHref(q.whatsapp, reviewMessage(q))} target="_blank" rel="noreferrer"
                             data-testid={`review-open-${q.id}`}
                             title="Pedir reseña"
                             className="btn-ghost-gold px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5">
                            <Star size={12} /> Reseña
                          </a>
                        )}
                        <a href={whatsappHref(q.whatsapp, waMessage(q))} target="_blank" rel="noreferrer"
                           data-testid={`wa-open-${q.id}`}
                           className="btn-ghost-gold px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5">
                          <MessageCircle size={12} /> WhatsApp
                        </a>
                        <button onClick={() => del(q.id)} data-testid={`delete-${q.id}`} className="p-1.5 rounded-md border border-[#3A3229] text-[#F5EFE0]/60 hover:border-rose-500/60 hover:text-rose-400"
                                style={{ transitionProperty: "border-color,color", transitionDuration: "200ms" }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 px-4" onClick={() => setSelected(null)}>
          <div className="card-lux w-full max-w-lg p-6 md:p-8 bg-[#171412]" onClick={(e) => e.stopPropagation()} data-testid="quote-detail">
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-2">{serviceLabel(selected.service_type)}</div>
            <div className="font-display text-2xl text-[#F5EFE0]">{selected.name}</div>
            <div className="text-sm text-[#F5EFE0]/60 mt-1">{selected.whatsapp}{selected.email ? ` · ${selected.email}` : ""}</div>
            <div className="divider-gold my-5" />
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div><dt className="text-[#F5EFE0]/50 text-xs">Origen</dt><dd>{selected.origin}</dd></div>
              <div><dt className="text-[#F5EFE0]/50 text-xs">Destino</dt><dd>{selected.destination || "—"}</dd></div>
              <div><dt className="text-[#F5EFE0]/50 text-xs">Fecha</dt><dd>{selected.date}</dd></div>
              <div><dt className="text-[#F5EFE0]/50 text-xs">Hora</dt><dd>{selected.time || "—"}</dd></div>
              <div><dt className="text-[#F5EFE0]/50 text-xs">Pasajeros</dt><dd>{selected.passengers}</dd></div>
              <div><dt className="text-[#F5EFE0]/50 text-xs">Horas</dt><dd>{selected.total_hours || "—"}</dd></div>
              {selected.flight_number && (
                <div className="col-span-2"><dt className="text-[#F5EFE0]/50 text-xs">Vuelo</dt><dd className="font-mono text-[#E8C87A]">{selected.flight_number}</dd></div>
              )}
            </dl>
            {selected.notes && (<><div className="text-[#F5EFE0]/50 text-xs mt-4">Notas</div><div className="text-sm mt-1">{selected.notes}</div></>)}

            <div className="mt-6 pt-5 border-t border-[#3A3229]">
              <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-3">Generar cotización</div>
              <div className="flex flex-wrap gap-2">
                <input
                  value={precioInput}
                  onChange={(e) => setPrecioInput(e.target.value)}
                  placeholder="Precio, ej: 2.200.000"
                  data-testid="precio-input"
                  className="bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2 text-sm flex-1 min-w-[140px] focus:border-[#D4AF37] outline-none"
                />
                <input
                  value={fechaFinInput}
                  onChange={(e) => setFechaFinInput(e.target.value)}
                  placeholder="Fecha fin (opcional, AAAA-MM-DD)"
                  data-testid="fecha-fin-input"
                  className="bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2 text-sm flex-1 min-w-[180px] focus:border-[#D4AF37] outline-none"
                />
              </div>
              <button
                onClick={generarCotizacion}
                disabled={generando}
                data-testid="generar-cotizacion-btn"
                className="btn-gold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 mt-3 disabled:opacity-60"
              >
                {generando ? <Loader2 size={14} className="animate-spin" /> : <ImageDown size={14} />}
                {generando ? "Generando…" : "Generar imagen de cotización"}
              </button>
            </div>

            {selected.status === "confirmada" && (
              <div className="mt-6 pt-5 border-t border-[#3A3229]">
                <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-3">Formulario de datos del servicio</div>
                <p className="text-xs text-[#F5EFE0]/55 mb-3">
                  Envíaselo justo después de aprobar la cotización, para que complete los datos necesarios antes del pago.
                </p>
                <a
                  href={whatsappHref(selected.whatsapp, formMessage(selected))}
                  target="_blank" rel="noreferrer"
                  data-testid="enviar-formulario-btn"
                  className="btn-gold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2"
                >
                  <MessageCircle size={14} /> Enviar formulario
                </a>
              </div>
            )}

            {selected.status === "confirmada" && (
              <div className="mt-6 pt-5 border-t border-[#3A3229]">
                <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-3">Ejecución del servicio</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <select
                    value={vehiculoInput}
                    onChange={(e) => setVehiculoInput(e.target.value)}
                    data-testid="vehiculo-select"
                    className="bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2 text-sm flex-1 min-w-[160px] text-[#F5EFE0] focus:border-[#D4AF37] outline-none"
                  >
                    <option value="">Vehículo (opcional)</option>
                    {fleet.map((f) => (
                      <option key={f.id} value={f.name}>{f.name} ({f.capacity} pax)</option>
                    ))}
                  </select>
                  <input
                    value={placaInput}
                    onChange={(e) => setPlacaInput(e.target.value)}
                    placeholder="Placa"
                    data-testid="placa-input"
                    className="bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2 text-sm flex-1 min-w-[100px] focus:border-[#D4AF37] outline-none"
                  />
                  <input
                    value={movilInput}
                    onChange={(e) => setMovilInput(e.target.value)}
                    placeholder="Móvil"
                    data-testid="movil-input"
                    className="bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2 text-sm flex-1 min-w-[100px] focus:border-[#D4AF37] outline-none"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <input
                    value={conductorNombreInput}
                    onChange={(e) => setConductorNombreInput(e.target.value)}
                    placeholder="Nombre del conductor"
                    data-testid="conductor-nombre-input"
                    className="bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2 text-sm flex-1 min-w-[140px] focus:border-[#D4AF37] outline-none"
                  />
                  <input
                    value={conductorTelefonoInput}
                    onChange={(e) => setConductorTelefonoInput(e.target.value)}
                    placeholder="WhatsApp del conductor"
                    data-testid="conductor-telefono-input"
                    className="bg-[#0A0908] border border-[#3A3229] rounded-lg px-3 py-2 text-sm flex-1 min-w-[140px] focus:border-[#D4AF37] outline-none"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={guardarAsignacion}
                    disabled={guardandoAsignacion}
                    data-testid="guardar-asignacion-btn"
                    className="btn-ghost-gold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    {guardandoAsignacion ? <Loader2 size={14} className="animate-spin" /> : null}
                    Guardar asignación
                  </button>
                  {conductorTelefonoInput && (
                    <a
                      href={whatsappHref(conductorTelefonoInput, fichaMessage({ ...selected, assigned_vehicle: vehiculoInput, vehicle_plate: placaInput, vehicle_movil: movilInput }))}
                      target="_blank" rel="noreferrer"
                      data-testid="enviar-ficha-btn"
                      className="btn-gold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2"
                    >
                      <Truck size={14} /> Enviar ficha al conductor
                    </a>
                  )}
                </div>
              </div>
            )}

            {selected.status === "confirmada" && (
              <div className="mt-6 pt-5 border-t border-[#3A3229]">
                <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-3">Post-venta</div>
                <p className="text-xs text-[#F5EFE0]/55 mb-3">
                  Úsalo cuando el viaje ya haya ocurrido, para pedirle una reseña.
                </p>
                <a
                  href={whatsappHref(selected.whatsapp, reviewMessage(selected))}
                  target="_blank" rel="noreferrer"
                  data-testid="pedir-resena-btn"
                  className="btn-gold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2"
                >
                  <Star size={14} /> Pedir reseña
                </a>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setSelected(null)} className="btn-ghost-gold px-4 py-2 rounded-full text-sm">Cerrar</button>
              <a href={whatsappHref(selected.whatsapp, waMessage(selected))} target="_blank" rel="noreferrer" className="btn-gold px-4 py-2 rounded-full text-sm">Abrir WhatsApp</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
